import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

const Blog = ({
  apiPath = "/blog/posts",
  basePath = "/blog",
  kicker = "Insights",
  heading = "Leadership in Practice",
  intro = "Field-tested writing on character-based leadership, emotional intelligence, and constructive conflict engagement from Dr. Andrew Campbell.",
  metaTitle = "Blog — Global Leader Solutions",
  metaDescription = "Character-based leadership, emotional intelligence, and conflict management insights from Dr. Andrew Campbell.",
  canonical,
  emptyLabel = "No posts yet.",
  heroCta = null,
  testIdPrefix = "blog",
}) => {
  useMeta({ title: metaTitle, description: metaDescription, canonical });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(apiPath).then(({ data }) => {
      setPosts(data.posts || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [apiPath]);

  return (
    <div data-testid={`${testIdPrefix}-index-page`} className="bg-white">
      <section className="bg-[#FAF4F5] border-b border-[#E8DCDD]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-4">{kicker}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-[#2D2628] leading-[1.05]">
            {heading}
          </h1>
          <p className="mt-5 text-base text-[#6B5E62] max-w-2xl leading-relaxed">{intro}</p>
          {heroCta}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading && <p className="text-sm text-[#6B5E62]">Loading…</p>}
        {!loading && posts.length === 0 && (
          <p className="text-sm text-[#6B5E62]" data-testid={`${testIdPrefix}-empty`}>{emptyLabel}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {posts.map((p) => (
            <article key={p.id} data-testid={`${testIdPrefix}-card-${p.slug}`}
              className="group border border-[#E8DCDD] rounded-md overflow-hidden bg-white hover:border-[#8C4A57] transition-colors duration-200">
              <Link to={`${basePath}/${p.slug}`} className="block">
                <div className="w-full aspect-[16/9] bg-[#FAF4F5] overflow-hidden">
                  {p.image && <img src={p.image} alt={p.image_alt || p.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                </div>
                <div className="p-6">
                  <h2 className="text-2xl font-serif font-semibold text-[#2D2628] leading-tight mb-3 group-hover:text-[#8C4A57] transition-colors">
                    {p.title}
                  </h2>
                  <p className="text-sm text-[#6B5E62] leading-relaxed line-clamp-3">{p.excerpt || p.description}</p>
                  <span className="mt-5 inline-block text-xs uppercase tracking-widest font-semibold text-[#8C4A57]">
                    Read the article →
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Blog;
